import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslationTaskProcessing } from '~/backend/connector-api/connector-components';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';

type UseImageTranslationTaskProps = {
    studyId: string;
    seriesId: string;
};

/**
 * A hook for creating an image translation task via Connector API.
 * @param {UseImageTranslationTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useImageTranslationTask({
    studyId,
    seriesId,
}: UseImageTranslationTaskProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useRecoilValue(primaryImageState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startTranslation } = useTranslationTaskProcessing();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet, setEnvironmentBlendImage } =
        useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                const generatedStructureSet = processedItems.structureSets[0];
                const generatedImage = processedItems.images[0];
                const currentPrimaryImage = primaryImage;

                if (generatedStructureSet) {
                    updateJob(job.id, { isImported: true });
                    setEnvironmentStructureSet(
                        [generatedStructureSet],
                        selectedPatientId ?? ''
                    );

                    if (generatedStructureSet.image === generatedImage) {
                        // Structure set references the generated image
                        setEnvironmentBlendImage(currentPrimaryImage!);
                    } else {
                        // Structure set references input image
                        setEnvironmentBlendImage(generatedImage);
                    }
                } else {
                    setEnvironmentBlendImage(generatedImage);
                }
            }
        }
    };

    /**
     * Creates the image translation task with the provided model.
     * @param {PredictionModel} selectedModel - The model to use for the image translation task.
     */
    const start = async (
        selectedModel: PredictionModel,
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            const modelIdentifier = customizationName
                ? `${selectedModel.name}.${customizationName}`
                : selectedModel.name;

            const task = await startTranslation({
                body: {
                    modelName: modelIdentifier,
                    imageSeries: {
                        seriesInstanceUID: seriesId,
                        studyInstanceUID: studyId,
                    },
                },
            });

            const newJob: NewJobData = {
                name: `Image+`,
                id: task.id,
                type: WorkflowTypes.IMAGE,
                task: {
                    model: selectedModel,
                    output: task,
                },
                onLoad: handleProcessedFiles,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleError(error, 'Failed to start image translation task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
